/// <reference types="vite/client" />
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, Zap } from "lucide-react";

const HEALTH_API =
  (import.meta.env.VITE_HEALTH_API as string | undefined) ??
  "https://admin-health.will-i-bracken.workers.dev";

type Status = "healthy" | "degraded" | "broken" | "idle" | "unknown";

interface SummaryResponse {
  healthy: number;
  degraded: number;
  broken: number;
  total: number;
  last_run_iso: string | null;
}

interface ServiceRow {
  id: string;
  name: string;
  url: string;
  ts: number | null;
  status: Status;
  latency_ms: number | null;
  http_code: number | null;
  error_msg: string | null;
}

interface AllResponse {
  services: ServiceRow[];
}

interface HistoryPoint {
  ts: number;
  status: Status;
  latency_ms: number | null;
}

const STATUS_STYLE: Record<Status, { dot: string; pill: string; label: string; stroke: string }> = {
  healthy:  { dot: "bg-accent-emerald", pill: "bg-accent-emerald/10 text-accent-emerald border-accent-emerald/20", label: "HEALTHY",  stroke: "#34d399" },
  degraded: { dot: "bg-accent-amber",   pill: "bg-accent-amber/10 text-accent-amber border-accent-amber/20",     label: "DEGRADED", stroke: "#fbbf24" },
  broken:   { dot: "bg-accent-rose",    pill: "bg-accent-rose/10 text-accent-rose border-accent-rose/20",         label: "BROKEN",   stroke: "#fb7185" },
  idle:     { dot: "bg-slate-400",      pill: "bg-slate-400/10 text-slate-400 border-slate-400/20",               label: "IDLE",     stroke: "#94a3b8" },
  unknown:  { dot: "bg-slate-600",      pill: "bg-slate-600/10 text-slate-500 border-slate-600/20",               label: "UNKNOWN",  stroke: "#64748b" },
};

const REFRESH_MS = 30_000;

export default function Health() {
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [services, setServices] = useState<ServiceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  async function load() {
    setRefreshing(true);
    try {
      const [s, a] = await Promise.all([
        fetch(`${HEALTH_API}/api/health/summary`).then((r) => r.json() as Promise<SummaryResponse>),
        fetch(`${HEALTH_API}/api/health/all`).then((r) => r.json() as Promise<AllResponse>),
      ]);
      setSummary(s);
      setServices(a.services);
      setError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "failed to load";
      setError(msg);
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  const lastRun = summary?.last_run_iso
    ? new Date(summary.last_run_iso).toLocaleString()
    : "never";

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Layer · Observability</div>
          <h1 className="text-3xl font-bold mt-1 tracking-tight">System Health</h1>
          <p className="text-slate-400 mt-2 max-w-2xl text-sm">
            Live probe of every service in the catalog. Cron pings every 5 minutes; this page refreshes every 30 seconds.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs font-mono text-slate-500">
          <span>last run: {lastRun}</span>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06] text-slate-300 hover:bg-white/[0.07] transition"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="glass p-4 ring-glass border-accent-rose/30 bg-accent-rose/[0.04] text-sm text-accent-rose/90">
          Failed to reach health API: {error}
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Healthy" value={summary?.healthy} loading={!summary} icon={<CheckCircle2 size={18} />} tone="emerald" />
        <Kpi label="Degraded" value={summary?.degraded} loading={!summary} icon={<Zap size={18} />} tone="amber" />
        <Kpi label="Broken" value={summary?.broken} loading={!summary} icon={<AlertTriangle size={18} />} tone="rose" />
        <Kpi label="Total" value={summary?.total} loading={!summary} icon={<Activity size={18} />} tone="blue" />
      </div>

      {/* Service grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services
          ? services.map((s, i) => <ServiceHealthCard key={s.id} svc={s} delay={i * 0.03} />)
          : Array.from({ length: 9 }).map((_, i) => <CardSkeleton key={i} />)}
      </div>
    </div>
  );
}

function Kpi({
  label, value, loading, icon, tone,
}: {
  label: string;
  value: number | undefined;
  loading: boolean;
  icon: React.ReactNode;
  tone: "emerald" | "amber" | "rose" | "blue";
}) {
  const toneClass = {
    emerald: "text-accent-emerald",
    amber: "text-accent-amber",
    rose: "text-accent-rose",
    blue: "text-accent-blue",
  }[tone];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass p-4 ring-glass"
    >
      <div className="flex items-center justify-between">
        <div className="eyebrow">{label}</div>
        <div className={toneClass}>{icon}</div>
      </div>
      <div className="mt-2 text-3xl font-bold tracking-tight">
        {loading ? <span className="inline-block h-8 w-12 rounded shimmer bg-white/[0.04]" /> : value ?? 0}
      </div>
    </motion.div>
  );
}

function ServiceHealthCard({ svc, delay }: { svc: ServiceRow; delay: number }) {
  const st = STATUS_STYLE[svc.status];
  const [history, setHistory] = useState<HistoryPoint[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        const r = await fetch(`${HEALTH_API}/api/health/${svc.id}`);
        const data = (await r.json()) as { id: string; history: HistoryPoint[] };
        if (!cancelled) setHistory(data.history);
      } catch {
        if (!cancelled) setHistory([]);
      }
    }
    run();
    const t = setInterval(run, REFRESH_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [svc.id]);

  const chartData = useMemo(
    () => (history ?? []).map((p) => ({ ts: p.ts, latency: p.latency_ms ?? 0 })),
    [history]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="glass glass-hover p-5 ring-glass"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold truncate">{svc.name}</h3>
          <div className="eyebrow mt-0.5 truncate">{svc.url.replace(/^https?:\/\//, "")}</div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${st.pill}`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${st.dot} mr-1.5 align-middle`} />
          {st.label}
        </span>
      </div>

      <div className="mt-3 flex items-center justify-between text-[11px] font-mono text-slate-500">
        <span>
          {svc.latency_ms != null ? `${svc.latency_ms} ms` : "—"}
          {svc.http_code != null && <span className="ml-2 text-slate-600">HTTP {svc.http_code}</span>}
        </span>
        <span>{svc.ts ? new Date(svc.ts).toLocaleTimeString() : "no data"}</span>
      </div>

      <div className="mt-3 h-[40px]">
        {history === null ? (
          <div className="h-full w-full rounded shimmer bg-white/[0.03]" />
        ) : chartData.length === 0 ? (
          <div className="h-full w-full flex items-center justify-center text-[10px] font-mono text-slate-600">
            no 24h history
          </div>
        ) : (
          <Sparkline data={chartData} stroke={st.stroke} id={svc.id} />
        )}
      </div>

      {svc.error_msg && (
        <div className="mt-2 px-2 py-1 rounded bg-accent-rose/[0.06] border border-accent-rose/[0.15] text-[10px] font-mono text-accent-rose/90 truncate">
          {svc.error_msg}
        </div>
      )}
    </motion.div>
  );
}

function Sparkline({
  data, stroke, id,
}: { data: Array<{ ts: number; latency: number }>; stroke: string; id: string }) {
  const W = 100;
  const H = 40;
  const PAD = 2;
  const max = Math.max(1, ...data.map((d) => d.latency));
  const n = data.length;
  const points = data.map((d, i) => {
    const x = n === 1 ? W / 2 : PAD + (i / (n - 1)) * (W - PAD * 2);
    const y = H - PAD - (d.latency / max) * (H - PAD * 2);
    return [x, y] as const;
  });
  const path = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const area =
    points.length > 0
      ? `${path} L${points[points.length - 1][0].toFixed(2)},${H} L${points[0][0].toFixed(2)},${H} Z`
      : "";
  const gradId = `grad-${id}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height="100%">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
          <stop offset="100%" stopColor={stroke} stopOpacity={0} />
        </linearGradient>
      </defs>
      {area && <path d={area} fill={`url(#${gradId})`} />}
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.25} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function CardSkeleton() {
  return (
    <div className="glass p-5 ring-glass">
      <div className="h-4 w-2/3 rounded shimmer bg-white/[0.04]" />
      <div className="mt-2 h-3 w-1/2 rounded shimmer bg-white/[0.03]" />
      <div className="mt-4 h-[40px] w-full rounded shimmer bg-white/[0.03]" />
    </div>
  );
}
